<script lang="ts" setup>
import {computed, ref, watch} from "vue";
import {db} from "@src/services/db";
import {useChatSessionStore} from "@src/stores/chatSession";
import PinnedMessage from "@src/components/views/HomeView/MainArea/HeaderArea/PinnedMessage.vue";
import {ChevronLeftIcon, EllipsisVerticalIcon} from "@heroicons/vue/24/outline";
import useStore from "@src/stores/store";

import TagManager from "@src/components/views/HomeView/MainArea/HeaderArea/TagManager.vue";
import {useContacts} from "@src/composables/useContacts";
import Contact from "@src/types/Contact";

const contactsComposable = useContacts()
const contacts = contactsComposable.getAllContacts()

const hasContact = computed(() => {
    return contacts.value?.some((contact: Contact) => contact.costumer_id === sessionStore.selectedSession?.costumer?.id)
})
const contact = computed(() => {
    return contacts.value?.find((contact: Contact) => contact.costumer_id === sessionStore.selectedSession?.costumer?.id)
})

const store = useStore()

const sessionStore = useChatSessionStore()

const isGroup = computed(() => {
    return sessionStore.type === 'group'
})

const picture = computed(() => {
    if (!isGroup.value) {
        return sessionStore.selectedSession?.costumer?.profile_picture
    } else {
        return (sessionStore.selectedSession as any)?.picture
    }
})

const groupChannel = ref<any | null>(null)

const selectedChannel = computed(() => {
    if (!isGroup.value) {
        return sessionStore.selectedSession?.channel ?? null
    }

    return sessionStore.selectedSession?.channel ?? groupChannel.value
})

watch(
    () => sessionStore.selectedSession,
    async (session: any) => {
        groupChannel.value = null

        if (!session || sessionStore.type !== 'group') {
            return
        }

        if (session.channel) {
            groupChannel.value = session.channel
            return
        }

        if (session.channel_id) {
            groupChannel.value = await db.channels.get(Number(session.channel_id))
        }
    },
    { immediate: true }
)

const displayName = computed(() => {
    if (isGroup.value) {
        return (sessionStore.selectedSession as any)?.name
    }

    return hasContact.value
        ? contact.value?.name
        : sessionStore.selectedSession?.costumer?.name
})

</script>

<template>
    <div class="w-full">
        <!--Top section-->
        <div class="w-full min-h-21 md:px-5 py-2 md:py-6">
            <div class="flex flex-row w-full justify-between text-sm dark:text-white items-center gap-2">
                <div class="flex flex-row items-center flex-shrink-0">
                    <div
                        class="mx-2 hover:bg-indigo-100 hover:text-indigo-500 text-gray-400 dark:text-white cursor-pointer rounded-full p-3"
                        @click="sessionStore.clearSession"
                    >
                        <ChevronLeftIcon
                            class="w-[20px] h-[20px] font-semibold"
                        />
                    </div>
                    <div
                        class="outline-none"
                    >
                        <div
                            :style="{ backgroundImage: `url(${picture})` }"
                            class="w-[2.25rem] h-[2.25rem] bg-cover bg-center rounded-full mr-4"
                        ></div>
                    </div>
                </div>

                <div class="flex-1 min-w-0">
                    <div class="flex flex-col min-w-0">
                        <div class="flex flex-col min-w-0">
                            <div class="flex flex-col md:flex-row md:items-center min-w-0 mb-2 md:mb-0">
                                <div class="flex flex-col xl:flex-row min-w-0">
                                    <span class="font-semibold mr-2 max-w-[180px] truncate inline-block">
                                        <template v-if="isGroup">
                                            {{ (sessionStore.selectedSession as any)?.name }}
                                        </template>

                                        <template v-else>
                                            {{ hasContact ? contact?.name : sessionStore.selectedSession?.costumer?.name }}
                                        </template>
                                    </span>

                                    <span v-if="!isGroup">
                                        {{
                                            sessionStore.selectedSession?.costumer?.username
                                                ? '@' + sessionStore.selectedSession?.costumer?.username
                                                : sessionStore.formattedToken(sessionStore.selectedSession?.costumer?.token)
                                        }}
                                    </span>
                                </div>

                                <div
                                    v-if="selectedChannel"
                                    class="hidden xl:block max-w-full overflow-x-auto scrollbar-hidden p-1 px-2 bg-gray-200 heading-2 text-xs rounded-sm ml-2 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap"
                                >
                                    Canal: {{ selectedChannel?.name }}
                                    <span
                                        v-if="selectedChannel?.type === 'whatsapp' || selectedChannel?.type === 'waapi'"
                                        class="ml-2"
                                    >- {{ selectedChannel?.token }}</span>
                                </div>
                            </div>

                            <div
                                v-if="selectedChannel"
                                class="xl:hidden mb-0.5 max-w-full overflow-x-auto scrollbar-hidden self-start p-1 px-2 bg-gray-200 heading-2 text-xs rounded-sm dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap"
                            >
                                Canal: {{ selectedChannel?.name }}
                                <span
                                    v-if="selectedChannel?.type === 'whatsapp' || selectedChannel?.type === 'waapi'"
                                    class="ml-2"
                                >- {{ selectedChannel?.token }}</span>
                            </div>
                        </div>

                        <TagManager v-if="!isGroup"/>
                    </div>
                </div>

                <div class="flex flex-row items-center flex-shrink-0">
                    <div
                        class="mx-2 hover:bg-indigo-100 hover:text-indigo-500 text-gray-400 dark:text-white cursor-pointer rounded-full p-3"
                        @click="store.details = true">
                        <EllipsisVerticalIcon
                            class="w-[20px] h-[20px] font-semibold"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!--Pinned Message-->
        <div
            class="relative transition-[padding] duration-200"
        >
            <PinnedMessage/>
        </div>

    </div>
</template>
